/**
 * SrHintBar — contextual help pill above the playback bar (Flow-style). The tip
 * changes with the active tool / selection and live with the held modifier keys
 * (Alt → move, Ctrl → erase).
 *
 * The keyboard icon on the right is a toggle with two modes:
 *   • PINNED (lit)  — the tooltip is always visible.
 *   • AUTO  (dim)   — the tooltip is hidden, and only fades in when you press
 *                     any key (stays 2s), or while the mouse hovers it; it fades
 *                     back out 2s after the last key / when the mouse leaves.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { isSynthVrView } from '../../store/editorSettings';
import { font } from '../ui/theme';
import { IcKeyboard } from '../ui/icons';

const PIN_KEY = 'sr-hint-pinned';

// ── External force-show signal ────────────────────────────────────────────────
// Lets callers (e.g. the collapse handle) make the hint bar visible regardless
// of pin/hover state, without requiring prop drilling through SrPlaybackBar.
let _externalForce = false;
const _forceListeners = new Set<(v: boolean) => void>();

export function setSrHintBarForced(on: boolean) {
  if (_externalForce === on) return;
  _externalForce = on;
  _forceListeners.forEach(fn => fn(on));
}

export default function SrHintBar() {
  const toolMode = useEditorStore(s => s.toolMode);
  const vr = useEditorStore(s => isSynthVrView(s.activeView));   // the new gestures are Synth VR's; Console keeps its hints
  const selCount = useEditorStore(s => s.selectedNoteIds.length);

  // Held-modifier (drives the hint text) + auto-hide visibility state.
  const [mod, setMod] = useState<'alt' | 'ctrl' | null>(null);
  const [pinned, setPinned] = useState<boolean>(() => {
    try { return localStorage.getItem(PIN_KEY) !== '0'; } catch { return true; }
  });
  const [hovered, setHovered] = useState(false);
  const [keyRecent, setKeyRecent] = useState(false);   // true for 2s after a keypress
  const [externalForced, setExternalForced] = useState(_externalForce);
  const hideTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const fn = (v: boolean) => setExternalForced(v);
    _forceListeners.add(fn);
    return () => { _forceListeners.delete(fn); };
  }, []);

  useEffect(() => {
    const sync = (e: KeyboardEvent) => {
      if (e.altKey) setMod('alt');
      else if (e.ctrlKey || e.metaKey) setMod('ctrl');
      else setMod(null);
      // Any key wakes the hint for 2s (only matters in auto-hide mode).
      setKeyRecent(true);
      window.clearTimeout(hideTimer.current);
      hideTimer.current = window.setTimeout(() => setKeyRecent(false), 2000);
    };
    const clear = () => setMod(null);
    window.addEventListener('keydown', sync);
    window.addEventListener('keyup', sync);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('keydown', sync);
      window.removeEventListener('keyup', sync);
      window.removeEventListener('blur', clear);
      window.clearTimeout(hideTimer.current);
    };
  }, []);

  const togglePinned = () => {
    setPinned(p => {
      const next = !p;
      try { localStorage.setItem(PIN_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  let hint: string;
  if (mod === 'alt') {
    hint = 'Alt — drag any note to move it · release to go back';
  } else if (mod === 'ctrl') {
    hint = vr ? 'Ctrl — right-click any note, rail node or wall to delete it' : 'Ctrl — click any note, rail or wall to erase it';
  } else if (selCount > 0) {
    hint = `${selCount} note${selCount > 1 ? 's' : ''} selected — ←/→/↑/↓ move on the grid · Del to remove · click empty space to deselect`;
  } else if (toolMode === 'select') {
    hint = vr
      ? 'Right-drag to set a loop (right-click to clear) · hold Z and step to select an area · wheel to scrub'
      : 'Select — left-drag to box-select (Shift add · Ctrl subtract) · right-drag to set a loop (right-click to clear) · wheel to scrub';
  } else if (toolMode === 'eraser') {
    hint = 'Eraser — click any note, rail or wall to delete it · right-drag to set a loop · wheel to scrub';
  } else {
    hint = vr
      ? 'Place — click to drop a note · right-drag to set a loop · wheel to scrub · Shift+wheel jumps note-to-note'
      : 'Place — click to drop a note · left-drag to box-select · right-drag to set a loop · wheel to scrub · Shift+wheel jumps note-to-note';
  }

  // Visible if pinned, hovered, key recently pressed, or forced by an external caller.
  const shown = pinned || hovered || keyRecent || externalForced;

  return (
    <div
      className="sr-hint-bar"
      style={{ ...barStyle, opacity: shown ? 1 : 0, transition: 'opacity 0.4s ease' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={{ opacity: 0.85 }}>💡</span>
      <span className="sr-hint-text">{hint}</span>
      <button
        onClick={togglePinned}
        title={pinned
          ? 'Tooltip pinned (always visible) — click to auto-hide'
          : 'Tooltip auto-hides — click to keep it always visible'}
        style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          padding: 2,
          cursor: 'pointer',
          color: 'var(--accent)',
          opacity: pinned ? 1 : 0.35,
          filter: pinned ? 'drop-shadow(0 0 5px var(--accent))' : 'none',
          transition: 'opacity 0.15s ease, filter 0.15s ease',
        }}
      >
        <IcKeyboard size={15} />
      </button>
    </div>
  );
}

const barStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '5px 14px',
  background: 'oklch(0.065 calc(0.016 * var(--tint-c)) var(--tint-h) / 0.78)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  borderRadius: 999,
  color: 'var(--tx-hi)',
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.2,
  lineHeight: 1.3,
  fontFamily: font.sans,
};
